/**
 * Panel API client for `/panel-api/v1` (see docs/panel-api.md).
 *
 * Responsibilities kept deliberately small:
 *   - `credentials: "include"` on every request (cookie session, never X-API-Key),
 *   - `X-CSRF-Token` on every mutation, taken from `csrf_token` in
 *     `GET /auth/session` (or login / 2FA verify) and refreshed once on a
 *     `403 csrf_failed`,
 *   - the error envelope `{"error":{"code","message","details"}}` turned into an
 *     `ApiError` so callers can branch on `code`,
 *   - a 401 hook for anything outside `/auth/*` so the app can bounce to /login.
 *
 * No React here — this module is unit-testable with a stubbed `fetch`.
 */

export const DEFAULT_BASE_URL = "/panel-api/v1";

/** Codes from docs/panel-api.md (+ the auth ones) that callers branch on. */
export const ERROR_CODES = Object.freeze({
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  notFound: "not_found",
  invalidRequest: "invalid_request",
  conflict: "conflict",
  quotaExceeded: "quota_exceeded",
  rateLimited: "rate_limited",
  csrfFailed: "csrf_failed",
  twoFactorRequired: "two_factor_required",
  // The account exists but has not confirmed its address, so the action that
  // would start outbound work was refused. The SPA answers it with the
  // verification banner rather than a bare failure.
  emailUnverified: "email_unverified",
  upstreamFailed: "upstream_failed",
  notConfigured: "not_configured",
  // A route that exists in the code but is switched off on this deployment.
  // Distinct from `forbidden`, which means "you may not", not "nobody may".
  featureDisabled: "feature_disabled",
  internalError: "internal_error",
});

export class ApiError extends Error {
  constructor({ status = 0, code = "internal_error", message = "", details = [] } = {}) {
    super(message || `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = Array.isArray(details) ? details : details == null ? [] : [details];
  }

  get isNotConfigured() {
    return this.code === ERROR_CODES.notConfigured;
  }

  get isUnauthorized() {
    return this.code === ERROR_CODES.unauthorized || this.status === 401;
  }

  get isNotFound() {
    return this.code === ERROR_CODES.notFound || this.status === 404;
  }

  get isFeatureDisabled() {
    return this.code === ERROR_CODES.featureDisabled;
  }
}

const STATUS_DEFAULT_CODE = {
  400: ERROR_CODES.invalidRequest,
  401: ERROR_CODES.unauthorized,
  403: ERROR_CODES.forbidden,
  404: ERROR_CODES.notFound,
  409: ERROR_CODES.conflict,
  410: ERROR_CODES.invalidRequest,
  429: ERROR_CODES.rateLimited,
  500: ERROR_CODES.internalError,
  502: ERROR_CODES.upstreamFailed,
  503: ERROR_CODES.notConfigured,
};

const STATUS_DEFAULT_MESSAGE = {
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to do that.",
  404: "Not found.",
  409: "That conflicts with something that already exists.",
  429: "Too many requests — try again in a moment.",
  500: "Something went wrong on our side.",
  502: "The upstream service failed.",
  503: "This feature is not configured on the server yet.",
};

function defaultCodeFor(status) {
  return STATUS_DEFAULT_CODE[status] || ERROR_CODES.internalError;
}

function defaultMessageFor(status) {
  return STATUS_DEFAULT_MESSAGE[status] || `Request failed with status ${status}`;
}

/** True for endpoints that must not trigger the global "go to /login" bounce. */
export function isAuthPath(path) {
  return path === "/auth" || path.startsWith("/auth/");
}

export function buildQuery(params = {}) {
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function createClient({
  baseUrl = DEFAULT_BASE_URL,
  fetchImpl,
  onUnauthorized = null,
} = {}) {
  let csrfToken = null;
  let unauthorizedHandler = onUnauthorized;
  const doFetch = (...args) => (fetchImpl || globalThis.fetch)(...args);

  function setCsrfToken(token) {
    csrfToken = token || null;
    return csrfToken;
  }

  function getCsrfToken() {
    return csrfToken;
  }

  /** Returns an unsubscribe function. */
  function setUnauthorizedHandler(handler) {
    unauthorizedHandler = handler || null;
    return () => {
      if (unauthorizedHandler === handler) unauthorizedHandler = null;
    };
  }

  function url(path) {
    return `${baseUrl}${path}`;
  }

  async function readPayload(response) {
    if (response.status === 204) return null;
    const contentType = response.headers?.get?.("content-type") || "";
    if (contentType.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return null;
      }
    }
    try {
      return await response.text();
    } catch {
      return null;
    }
  }

  async function toResult(response) {
    const payload = await readPayload(response);
    if (response.ok) return { data: payload, error: null };
    const envelope = payload && typeof payload === "object" && payload.error ? payload.error : {};
    return {
      data: null,
      error: new ApiError({
        status: response.status,
        code: envelope.code || defaultCodeFor(response.status),
        message: envelope.message || defaultMessageFor(response.status),
        details: envelope.details,
      }),
    };
  }

  async function send(path, { method = "GET", body, signal, redirect, keepalive } = {}) {
    const headers = { Accept: "application/json" };
    const mutating = !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (mutating && csrfToken) headers["X-CSRF-Token"] = csrfToken;

    return doFetch(url(path), {
      method,
      headers,
      credentials: "include",
      signal,
      keepalive,
      redirect: redirect || "follow",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /** Re-read `GET /auth/session` to pick up a fresh `csrf_token`. */
  async function refreshCsrfToken() {
    try {
      const response = await send("/auth/session");
      const { data } = await toResult(response);
      if (data && typeof data === "object" && data.csrf_token) setCsrfToken(data.csrf_token);
    } catch {
      /* The retry will fail with the same csrf_failed error and be surfaced. */
    }
  }

  async function request(path, options = {}) {
    const response = await send(path, options);
    let { data, error } = await toResult(response);

    if (
      error &&
      error.status === 403 &&
      error.code === ERROR_CODES.csrfFailed &&
      !options._csrfRetried
    ) {
      await refreshCsrfToken();
      return request(path, { ...options, _csrfRetried: true });
    }

    if (error && error.status === 401 && !isAuthPath(path)) {
      unauthorizedHandler?.(error);
    }

    if (error) throw error;

    if (data && typeof data === "object" && typeof data.csrf_token === "string") {
      setCsrfToken(data.csrf_token);
    }
    return data;
  }

  async function download(path) {
    const response = await send(path);
    if (!response.ok) {
      const { error } = await toResult(response);
      if (error && error.status === 401 && !isAuthPath(path)) unauthorizedHandler?.(error);
      throw error;
    }
    if (typeof response.blob === "function") return response.blob();
    return response.arrayBuffer();
  }

  /**
   * `GET /auth/oauth/{provider}/start` answers one of two ways.
   *
   * This client sends `Accept: application/json`, so the endpoint answers 200
   * with `{authorize_url}`, the absolute provider URL. A caller that only knew
   * about the redirect shape left the button apparently dead: the request
   * succeeded and the browser was never sent anywhere. The provider URL already
   * carries the one-time state, so navigating straight to it is also better than
   * the redirect fallback, which re-runs `/start` and mints a second state.
   *
   * A browser fetch with `redirect: "manual"` exposes a real redirect as an
   * opaque response (status 0 / type "opaqueredirect"); Node/undici surfaces a
   * plain 3xx. Both still mean "configured — send the browser to the endpoint".
   */
  async function startOAuth(provider) {
    const path = `/auth/oauth/${encodeURIComponent(provider)}/start`;
    const response = await send(path, { redirect: "manual" });
    const status = response.status;
    const redirected =
      response.type === "opaqueredirect" || status === 0 || (status >= 300 && status < 400);
    if (redirected) return { redirectUrl: url(path) };

    const { data, error } = await toResult(response);
    if (error) throw error;
    if (data && typeof data === "object" && typeof data.authorize_url === "string" && data.authorize_url) {
      return { redirectUrl: data.authorize_url };
    }
    return { data };
  }

  return {
    baseUrl,
    url,
    setCsrfToken,
    getCsrfToken,
    setUnauthorizedHandler,
    get: (path, options) => request(path, { ...options, method: "GET" }),
    post: (path, body, options) => request(path, { ...options, method: "POST", body }),
    put: (path, body, options) => request(path, { ...options, method: "PUT", body }),
    patch: (path, body, options) => request(path, { ...options, method: "PATCH", body }),
    del: (path, options) => request(path, { ...options, method: "DELETE" }),
    download,
    startOAuth,
  };
}
